import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import moment from "moment";
import { includes } from "lodash";
import { isDynamicDate } from "@/services/parameters/DateParameter";
import DateInput from "@/components/DateInput";
import MonthInput from "@/components/MonthInput";
import DateTimeInput from "@/components/DateTimeInput";
import DynamicButton from "@/components/dynamic-parameters/DynamicButton";

import "./DynamicParameters.less";

function getDisabledDate(dateSelectionRange, isMonth) {
  if (!dateSelectionRange || (!dateSelectionRange.start && !dateSelectionRange.end)) {
    return undefined;
  }

  const start = dateSelectionRange.start ? moment(dateSelectionRange.start, moment.ISO_8601, true) : null;
  const end = dateSelectionRange.end ? moment(dateSelectionRange.end, moment.ISO_8601, true) : null;
  const validStart = start && start.isValid() ? start : null;
  const validEnd = end && end.isValid() ? end : null;

  if (!validStart && !validEnd) {
    return undefined;
  }

  return current => {
    if (!current) {
      return false;
    }

    const unit = isMonth ? "month" : "day";
    return (
      (validStart && current.isBefore(validStart, unit)) ||
      (validEnd && current.isAfter(validEnd, unit))
    );
  };
}

class DynamicDatePicker extends React.Component {
  static propTypes = {
    type: PropTypes.string,
    className: PropTypes.string,
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    parameter: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    onSelect: PropTypes.func,
    dynamicButtonOptions: PropTypes.shape({
      staticValueLabel: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.object,
          label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        })
      ),
    }),
    dateOptions: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  };

  static defaultProps = {
    type: "",
    className: "",
    value: null,
    parameter: null,
    dynamicButtonOptions: {
      options: [],
    },
    onSelect: () => {},
  };

  constructor(props) {
    super(props);
    this.dateComponentRef = React.createRef();
  }

  onDynamicValueSelect = dynamicValue => {
    const { onSelect, parameter } = this.props;
    if (dynamicValue === "static") {
      const parameterValue = parameter.getExecutionValue();
      if (parameterValue) {
        onSelect(moment(parameterValue));
      } else {
        onSelect(null);
      }
    } else {
      onSelect(dynamicValue.value);
    }
    // give focus to the DatePicker to get keyboard shortcuts to work
    this.dateComponentRef.current.focus();
  };

  render() {
    const { type, value, className, dateOptions, dynamicButtonOptions, onSelect, parameter } = this.props;
    const hasDynamicValue = isDynamicDate(value);
    const isDateTime = includes(type, "datetime");
    const isMonth = parameter && parameter.dateGranularity === "month";
    const disabledDate = parameter && getDisabledDate(parameter.dateSelectionRange, isMonth);

    const additionalAttributes = {};
    if (disabledDate) {
      additionalAttributes.disabledDate = disabledDate;
    }

    let DateComponent = DateInput;
    if (isMonth) {
      DateComponent = MonthInput;
    } else if (isDateTime) {
      DateComponent = DateTimeInput;
      if (includes(type, "with-seconds")) {
        additionalAttributes.withSeconds = true;
      }
    }

    if (moment.isMoment(value) || value === null) {
      additionalAttributes.value = value;
    }

    if (hasDynamicValue) {
      const dynamicDate = value;
      additionalAttributes.placeholder = dynamicDate && dynamicDate.name;
      additionalAttributes.value = null;
    }

    const dynamicOptions = disabledDate
      ? dynamicButtonOptions.options.filter(option => !option.value || !disabledDate(option.value.value()))
      : dynamicButtonOptions.options;

    return (
      <div className={classNames("date-parameter", className)}>
        <DateComponent
          {...dateOptions}
          ref={this.dateComponentRef}
          className={classNames("redash-datepicker", type, { "dynamic-value": hasDynamicValue })}
          onSelect={onSelect}
          suffixIcon={null}
          {...additionalAttributes}
        />
        <DynamicButton
          options={dynamicOptions}
          staticValueLabel={dynamicButtonOptions.staticValueLabel}
          selectedDynamicValue={hasDynamicValue ? value : null}
          enabled={hasDynamicValue}
          onSelect={this.onDynamicValueSelect}
        />
      </div>
    );
  }
}

export default DynamicDatePicker;
